import React, { createContext, useContext } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

/** Shared primitives for the Lending Network page — dark zone inside the light site. */

const AudienceCtx = createContext({ audience: "introducer", setAudience: () => {}, isLender: false });

export const useAudience = () => useContext(AudienceCtx);

export function AudienceProvider({ value, children }) {
  return <AudienceCtx.Provider value={{ ...value, isLender: value.audience === "lender" }}>{children}</AudienceCtx.Provider>;
}

/** Film grain + soft beams — the texture that stops the dark reading as flat black. */
export function Ambience({ beams = true }) {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.045]"
           style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
      {beams && (
        <>
          <div className="absolute -top-56 -left-40 h-[560px] w-[560px] rounded-full bg-[#0055FF] opacity-[0.13] blur-[130px]" />
          <div className="absolute top-1/3 -right-52 h-[520px] w-[520px] rounded-full bg-[#7da9ff] opacity-[0.07] blur-[140px]" />
        </>
      )}
      <div className="absolute inset-0 mix-blend-overlay opacity-[0.35]"
           style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")" }} />
    </div>
  );
}

export function Mono({ children, className = "" }) {
  return (
    <span className={`font-mono text-[10.5px] uppercase tracking-[0.22em] text-[#7da9ff] ${className}`}>
      {children}
    </span>
  );
}

export function Eyebrow({ children }) {
  return (
    <div className="inline-flex items-center gap-2.5">
      <span className="h-px w-7 bg-[#7da9ff]/50" />
      <Mono>{children}</Mono>
    </div>
  );
}

/** Section shell — consistent rhythm, generous air, left-aligned by default. */
export function Section({ id, eyebrow, title, lede, children, className = "", tight = false, testId }) {
  return (
    <section id={id} data-testid={testId}
             className={`relative mx-auto max-w-7xl px-5 sm:px-8 ${tight ? "py-12 sm:py-14" : "py-16 sm:py-20"} ${className}`}>
      {(eyebrow || title) && (
        <Reveal className="max-w-3xl">
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          {title && (
            <h2 className="mt-4 font-cabinet text-[32px] sm:text-[44px] leading-[1.04] tracking-[-0.03em] font-semibold text-white">
              {title}
            </h2>
          )}
          {lede && <p className="mt-4 text-[15px] sm:text-base text-slate-400 leading-relaxed max-w-2xl">{lede}</p>}
        </Reveal>
      )}
      {children}
    </section>
  );
}

/** One scroll-reveal to rule them all. */
export function Reveal({ children, delay = 0, y = 18, className = "", as = "div" }) {
  const reduce = useReducedMotion();
  const M = motion[as] || motion.div;
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <M
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </M>
  );
}

/** Crossfades copy when the audience switch flips. */
export function Swap({ k, children, className = "" }) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={k}
        className={className}
        initial={{ opacity: 0, y: 10, filter: "blur(6px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        exit={{ opacity: 0, y: -8, filter: "blur(6px)" }}
        transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

/** Glass card — 1px stroke, saturated blur, hover lift. */
export function Glass({ children, className = "", hover = true, testId }) {
  return (
    <div data-testid={testId}
         className={`relative rounded-2xl border border-white/10 bg-white/[0.035] backdrop-blur-xl backdrop-saturate-150 ${
           hover ? "transition-[transform,border-color,background-color] duration-500 hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.06]" : ""} ${className}`}>
      {children}
    </div>
  );
}

export function HairRule({ className = "" }) {
  return <div className={`h-px w-full bg-gradient-to-r from-transparent via-white/12 to-transparent ${className}`} />;
}

const OPTIONS = [
  { k: "introducer", label: "I introduce deals" },
  { k: "lender", label: "I fund deals" },
];

/** The switch that re-words the whole page. Inline in the hero, floating once you scroll. */
export function AudienceSwitch({ floating = false }) {
  const { audience, setAudience } = useAudience();
  return (
    <div className={`relative flex items-center gap-1 rounded-full border p-1 ${
      floating
        ? "inline-flex border-white/12 bg-[#0B1220]/90 backdrop-blur-xl backdrop-saturate-150 shadow-[0_20px_60px_-18px_rgba(0,0,0,0.95)]"
        : "w-full max-w-sm sm:inline-flex sm:w-auto border-white/12 bg-white/[0.04]"}`}>
      {OPTIONS.map((o) => {
        const on = audience === o.k;
        return (
          <button key={o.k} type="button" onClick={() => setAudience(o.k)}
                  data-testid={`${floating ? "audience-float" : "audience-toggle"}-${o.k}`}
                  className={`relative z-10 rounded-full font-semibold transition-colors duration-300 ${
                    floating ? "h-9 px-4 sm:px-5 text-[12.5px]" : "flex-1 sm:flex-none h-9 sm:h-8 px-3 sm:px-4 text-[12.5px] sm:text-[12px]"} ${
                    on ? "text-white" : "text-slate-400 hover:text-slate-200"}`}>
            {on && (
              <motion.span layoutId={floating ? "aud-pill-float" : "aud-pill-inline"} aria-hidden
                           className="absolute inset-0 rounded-full bg-[#0055FF]"
                           transition={{ type: "spring", stiffness: 380, damping: 32 }} />
            )}
            <span className="relative">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}
